import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { EventStagesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { EventStageFilters } from '@/types'
import { Button } from '@/components/base/button'
import { EditDialog } from './edit_dialog'
import { NewDialog } from './new_dialog'
import { useTable } from '@/helpers/useTable'
import { dedupe } from '@/helpers/dedupe'

interface Props extends TableProps {
  eventId: string
  createable?: boolean
  updateable?: boolean
}

export const Table = ({ pageSize = 10, eventId, createable = false, updateable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<EventStagesQuery>(
    Query,
    { variables: { eventId, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openEdit, setOpenEdit } = useTable({
    pageInfo: data?.events.nodes[0].eventStages.pageInfo
  })
  const [eventStageId, setEventStageId] = useState<string>()

  const editEventStage = (id: string) => {
    if (!updateable) return

    setEventStageId(id)
    setOpenEdit(true)
  }

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  return (
    <>
      <BaseTable<EventStageFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Name' }]}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.events.nodes[0].eventStages.nodes = dedupe([
              ...previousData.events.nodes[0].eventStages.nodes,
              ...fetchMoreResult.events.nodes[0].eventStages.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Main stage? Big top?'
          },
        ]}
        rows={data.events.nodes[0].eventStages.nodes.map(eventStage => (
          {
            id: eventStage.id,
            columns: [
              eventStage.name,
            ]
          }
        ))}
        onRowClick={editEventStage}
      />
      { eventStageId && <EditDialog eventId={eventId} id={eventStageId} open={openEdit} setOpen={setOpenEdit} /> }
      <NewDialog eventId={eventId} open={openNew} setOpen={setOpenNew} />
    </>
  )
}
